import { GeographyService } from "../../geography";
import RegionSet, { RegionGranularity } from "../regionSet";

const geoService = new GeographyService();

/** Represents the possible check states for a region set. */
enum CheckStatus {
  Checked       = "checked",
  Unchecked     = "unchecked",
  Indeterminate = "indeterminate",
}

/** The general structure of the children inside a LookupRegionSet. */
interface LookupRegionSetChildren { [key: string]: LookupRegionSet; }

/**
 * A LookupRegionSet is a morphing of the standard RegionSet in favor of fast lookups.
 * It, in particular, transforms the children into a dictionary structure keyed off their (region code, granularity) combination.
 */
export default class LookupRegionSet {
  constructor(
    public regionCode: string,
    public granularity: RegionGranularity,
    public children: LookupRegionSetChildren = {},
    public subRegionCode: string = null,
  ) {
  }

  /** Produces the key for the lookup. */
  public get key(): string {
    return keyFor(this.regionCode, this.granularity, this.subRegionCode);
  }

  /**
   * Removes a child matching a given key.
   * @param key The child's key.
   */
  public removeChild(key: string): LookupRegionSet {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: _, ...newChildren } = this.children;

    return new LookupRegionSet(this.regionCode, this.granularity, newChildren, this.subRegionCode);
  }

  /** Adds or replaces an existing LookupRegionSet inside the children for the current LookupRegionSet. */
  public addOrReplaceChild(lookupRegionSet: LookupRegionSet): LookupRegionSet;

  /** Creates a LookupRegionSet from the provided parameters and adds or replaces it inside the children of the current LookupRegionSet. */
  public addOrReplaceChild(regionCode: string, granularity: RegionGranularity, children?: LookupRegionSetChildren, subRegionCode?: string): LookupRegionSet;

  /** Implementation of overloads. */
  public addOrReplaceChild(entity: LookupRegionSet | string, granularity?: RegionGranularity, children: LookupRegionSetChildren = {}, subRegionCode: string = null): LookupRegionSet {
    let child: LookupRegionSet;
    if (typeof entity === "string") {
      const regionCode = entity;
      child = new LookupRegionSet(regionCode, granularity, children, subRegionCode);
    } else {
      child = entity;
    }

    const newChildren = { ...this.children, [child.key]: child };
    return new LookupRegionSet(this.regionCode, this.granularity, newChildren, this.subRegionCode);
  }

  /** Converts this LookupRegionSet into a RegionSet. */
  public toRegionSet(): RegionSet {
    const partialRegionSets = Object.keys(this.children)
      .map((key) => this.children[key].toRegionSet());

    return {
      granularity: this.granularity,
      partialRegionSets,
      regionCode: this.regionCode,
      regionSetId: null,
      subRegionCode: this.subRegionCode,
    };
  }
}

async function findStateCheckStatus(state: LookupRegionSet): Promise<CheckStatus> {
  if (!state) {
    return CheckStatus.Unchecked;
  }

  const childKeys = Object.keys(state.children);

  // No children? While technically one could say this is the same as the whole state being checked...
  // We have to have an CheckStatus.Unchecked state and that exists via the zero state.
  if (childKeys.length === 0) {
    return CheckStatus.Unchecked;
  }

  const zips = await geoService.getZipsByState(state.regionCode);
  const childZips = childKeys
    .filter((key) => state.children[key].granularity === RegionGranularity.Zip)
    .map((key) => state.children[key]);

  // If the user has selected all zips, they've selected the whole state.
  // If they haven't selected all the zips, it's going to be CheckStatus.Indeterminate unless all the counties are selected. As such, fall through.
  if (childZips.length === zips.length) {
    return CheckStatus.Checked;
  }

  // Check the county selection.
  const counties = await geoService.getCountiesByState(state.regionCode);
  for (const county of counties) {
    const childLookup = state.children[keyFor(county.countySSA, RegionGranularity.County)];

    // If there's no child, then we don't have the full state.
    // If the child isn't checked, then we don't have the full state.
    if (!childLookup) {
      return CheckStatus.Indeterminate;
    }
  }

  return CheckStatus.Checked;
}

async function findCbsaCheckStatus(cbsa: LookupRegionSet): Promise<CheckStatus> {
  if (!cbsa) {
    return CheckStatus.Unchecked;
  }

  const childKeys = Object.keys(cbsa.children);

  // No children? While technically one could say this is the same as the whole CBSA being checked...
  // We have to have an CheckStatus.Unchecked CBSA and that exists via the zero state.
  if (childKeys.length === 0) {
    return CheckStatus.Unchecked;
  }

  const counties = await geoService.getCountiesByCbsa(cbsa.regionCode);

  // Check the county selection.
  for (const county of counties) {
    const childLookup = cbsa.children[keyFor(county.countySSA, RegionGranularity.County)];

    // If there's no child, then we don't have the full state.
    // If the child isn't checked, then we don't have the full state.
    if (!childLookup) {
      return CheckStatus.Indeterminate;
    }
  }

  return CheckStatus.Checked;
}

async function findZipCheckStatus(zip?: LookupRegionSet): Promise<CheckStatus> {
  // If we've got a zip here, it must be considered checked.
  return !!zip
    ? CheckStatus.Checked
    : CheckStatus.Unchecked;
}

async function findCountyZipCheckStatus(countyZip?: LookupRegionSet): Promise<CheckStatus> {
  // If we've got a county-zip here, it must be considered checked.
  return !!countyZip
    ? CheckStatus.Checked
    : CheckStatus.Unchecked;
}

/**
 * Produces the access key for a given region code and granularity to which it applies.
 * @param regionCode The region code in question.
 * @param granularity The granularity that the region code is a descriptor for.
 * @param subRegionCode An optional sub-region code for county-zips.
 */
function keyFor(regionCode: string, granularity: RegionGranularity, subRegionCode?: string): string;

/**
 * Produces the access key for a given region set.
 * @param regionSet The region set in question.
 */
function keyFor(regionSet: RegionSet): string;

/**
 * Produces an access key for a given entity, either string or RegionSet.
 */
function keyFor(entity: string | RegionSet, granularity?: RegionGranularity, subRegionCode?: string): string {
  if (typeof entity === "string" || !entity) {
    return subRegionCode
      ? `(${entity}, ${subRegionCode}, ${granularity})`
      : `(${entity}, ${granularity})`;
  }

  return entity.granularity === RegionGranularity.CountyZip
    ? `(${entity.regionCode}, ${entity.subRegionCode}, ${entity.granularity})`
    : `(${entity.regionCode}, ${entity.granularity})`;
}

export {
  CheckStatus,
  findStateCheckStatus,
  findCbsaCheckStatus,
  findZipCheckStatus,
  findCountyZipCheckStatus,
  keyFor,
};
